import { createClient } from '@supabase/supabase-js'
import type {
  ChatSession, ChatMessage, Task, MetricsHistory, PromptsLibrary,
  UserPreferences, VoiceProfile, Skillset, AgentBlueprint, Workspace, UIPreferences
} from './database.types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not configured. Auth features disabled.')
}

// Using untyped client for flexibility - tables created via schema.sql
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  }
)

// Service client alias (RLS now allows anon read for Claude tables)
export const supabaseService = supabase

// Expose supabase client globally for widgets (voice-overlay.js)
declare global {
  interface Window {
    __SUPABASE_CLIENT__: typeof supabase
  }
}
if (typeof window !== 'undefined') {
  window.__SUPABASE_CLIENT__ = supabase
}

export const isSupabaseConfigured = () => !!(supabaseUrl && supabaseAnonKey)

// Chat Sessions
export const chatDb = {
  async createSession(userId: string, title?: string): Promise<ChatSession | null> {
    const { data, error } = await supabase
      .from('chat_sessions')
      .insert({ user_id: userId, title })
      .select()
      .single()
    if (error) { console.error('createSession error:', error); return null }
    return data
  },

  async getSession(sessionId: string): Promise<ChatSession | null> {
    const { data, error } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('id', sessionId)
      .single()
    if (error) return null
    return data
  },

  async getUserSessions(userId: string): Promise<ChatSession[]> {
    const { data, error } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
    if (error) return []
    return data || []
  },

  async addMessage(sessionId: string, role: 'user' | 'assistant' | 'system', content: string, provider?: string, model?: string): Promise<ChatMessage | null> {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({ session_id: sessionId, role, content, provider, model })
      .select()
      .single()
    if (error) { console.error('addMessage error:', error); return null }
    await supabase.from('chat_sessions').update({ updated_at: new Date().toISOString() }).eq('id', sessionId)
    return data
  },

  async getMessages(sessionId: string): Promise<ChatMessage[]> {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('timestamp', { ascending: true })
    if (error) return []
    return data || []
  },

  async deleteSession(sessionId: string): Promise<boolean> {
    const { error } = await supabase.from('chat_sessions').delete().eq('id', sessionId)
    return !error
  }
}

// Tasks
export const taskDb = {
  async create(userId: string, prompt: string, priority = 0, source?: string): Promise<Task | null> {
    const { data, error } = await supabase
      .from('tasks')
      .insert({ user_id: userId, prompt, priority, source })
      .select()
      .single()
    if (error) { console.error('task create error:', error); return null }
    return data
  },

  async update(taskId: string, updates: { status?: Task['status']; output?: string; completed_at?: string }): Promise<Task | null> {
    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
      .select()
      .single()
    if (error) return null
    return data
  },

  async getPending(userId: string): Promise<Task[]> {
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['pending', 'processing'])
      .order('priority', { ascending: false })
      .order('created_at', { ascending: true })
    return data || []
  },

  async getRecent(userId: string, limit = 50): Promise<Task[]> {
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)
    return data || []
  }
}

// Metrics
export const metricsDb = {
  async record(data: Omit<MetricsHistory, 'id' | 'timestamp'>): Promise<MetricsHistory | null> {
    const { data: result, error } = await supabase
      .from('metrics_history')
      .insert(data)
      .select()
      .single()
    if (error) return null
    return result
  },

  async getRecent(limit = 100, userId?: string): Promise<MetricsHistory[]> {
    let query = supabase
      .from('metrics_history')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(limit)
    if (userId) query = query.eq('user_id', userId)
    const { data } = await query
    return data || []
  }
}

// Prompts Library
export const promptsDb = {
  async create(userId: string, name: string, promptText: string, category?: string, description?: string, tags?: string[]): Promise<PromptsLibrary | null> {
    const { data, error } = await supabase
      .from('prompts_library')
      .insert({ user_id: userId, name, prompt_text: promptText, category, description, tags })
      .select()
      .single()
    if (error) { console.error('prompt create error:', error); return null }
    return data
  },

  async getAll(userId: string): Promise<PromptsLibrary[]> {
    const { data } = await supabase
      .from('prompts_library')
      .select('*')
      .eq('user_id', userId)
      .order('usage_count', { ascending: false })
    return data || []
  },

  async getByCategory(userId: string, category: string): Promise<PromptsLibrary[]> {
    const { data } = await supabase
      .from('prompts_library')
      .select('*')
      .eq('user_id', userId)
      .eq('category', category)
    return data || []
  },

  async incrementUsage(promptId: string): Promise<void> {
    const { data } = await supabase.from('prompts_library').select('usage_count').eq('id', promptId).single()
    if (data) {
      await supabase.from('prompts_library').update({
        usage_count: (data.usage_count || 0) + 1,
        last_used_at: new Date().toISOString()
      }).eq('id', promptId)
    }
  },

  async update(promptId: string, updates: Partial<Pick<PromptsLibrary, 'name' | 'category' | 'prompt_text' | 'description' | 'tags'>>): Promise<PromptsLibrary | null> {
    const { data, error } = await supabase
      .from('prompts_library')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', promptId)
      .select()
      .single()
    if (error) return null
    return data
  },

  async delete(promptId: string): Promise<boolean> {
    const { error } = await supabase.from('prompts_library').delete().eq('id', promptId)
    return !error
  }
}

// User Preferences - Cloud sync for all user data (multi-tenant via user.id)
export type { VoiceProfile, Skillset, AgentBlueprint, Workspace, UIPreferences, UserPreferences }

export const preferencesDb = {
  // Get all preferences at once
  async getAll(): Promise<UserPreferences | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('profiles')
      .select('preferences')
      .eq('id', user.id)
      .single()

    if (error || !data?.preferences) return null
    return data.preferences as UserPreferences
  },

  // Save all preferences at once (full replace)
  async saveAll(prefs: UserPreferences): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false

    const { error } = await supabase
      .from('profiles')
      .update({ preferences: prefs, updated_at: new Date().toISOString() })
      .eq('id', user.id)

    return !error
  },

  // Generic update - merges with existing preferences
  async update<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false

    const { data: existing } = await supabase
      .from('profiles')
      .select('preferences')
      .eq('id', user.id)
      .single()

    const currentPrefs = (existing?.preferences || {}) as UserPreferences
    const updatedPrefs = { ...currentPrefs, [key]: value }

    const { error } = await supabase
      .from('profiles')
      .update({ preferences: updatedPrefs, updated_at: new Date().toISOString() })
      .eq('id', user.id)

    return !error
  },

  // Generic delete key
  async clear<K extends keyof UserPreferences>(key: K): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false

    const { data: existing } = await supabase
      .from('profiles')
      .select('preferences')
      .eq('id', user.id)
      .single()

    const currentPrefs = (existing?.preferences || {}) as UserPreferences
    delete currentPrefs[key]

    const { error } = await supabase
      .from('profiles')
      .update({ preferences: currentPrefs, updated_at: new Date().toISOString() })
      .eq('id', user.id)

    return !error
  },

  // Voice profile shortcuts (backwards compatible)
  async getVoiceProfile(): Promise<VoiceProfile | null> {
    const prefs = await this.getAll()
    return prefs?.voice_profile || null
  },

  async saveVoiceProfile(profile: VoiceProfile): Promise<boolean> {
    return this.update('voice_profile', profile)
  },

  async clearVoiceProfile(): Promise<boolean> {
    return this.clear('voice_profile')
  },

  // Skillsets
  async getSkillsets(): Promise<Record<string, Skillset>> {
    const prefs = await this.getAll()
    return prefs?.skillsets || {}
  },

  async saveSkillsets(skillsets: Record<string, Skillset>): Promise<boolean> {
    return this.update('skillsets', skillsets)
  },

  // Agents
  async getAgents(): Promise<Record<string, AgentBlueprint>> {
    const prefs = await this.getAll()
    return prefs?.agents || {}
  },

  async saveAgents(agents: Record<string, AgentBlueprint>): Promise<boolean> {
    return this.update('agents', agents)
  },

  // Workspaces
  async getWorkspaces(): Promise<Record<string, Workspace>> {
    const prefs = await this.getAll()
    return prefs?.workspaces || {}
  },

  async saveWorkspaces(workspaces: Record<string, Workspace>): Promise<boolean> {
    return this.update('workspaces', workspaces)
  },

  // Custom instructions
  async getCustomInstructions(): Promise<string | null> {
    const prefs = await this.getAll()
    return prefs?.custom_instructions || null
  },

  async saveCustomInstructions(instructions: string): Promise<boolean> {
    return this.update('custom_instructions', instructions)
  },

  // UI preferences
  async getUIPreferences(): Promise<UIPreferences> {
    const prefs = await this.getAll()
    return prefs?.ui || {}
  },

  async saveUIPreferences(ui: UIPreferences): Promise<boolean> {
    return this.update('ui', ui)
  }
}

// Code Vault - Indexed components from across the VPS
export interface CodeVaultEntry {
  id: string
  user_id: string
  name: string
  slug: string
  description: string | null
  content: string
  language: string
  file_path: string | null
  tags: string[]
  is_template: boolean
  is_component: boolean
  component_type: string | null
  source_task_id: string | null
  source_type: string
  lines_of_code: number
  size_bytes: number
  version: number
  created_at: string
  updated_at: string
}

export const vaultDb = {
  async getAll(limit = 500, offset = 0): Promise<CodeVaultEntry[]> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)
    if (error) { console.error('vault getAll error:', error); return [] }
    return data || []
  },

  async getByType(componentType: string, limit = 100, offset = 0): Promise<CodeVaultEntry[]> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('*')
      .eq('component_type', componentType)
      .order('name', { ascending: true })
      .range(offset, offset + limit - 1)
    if (error) return []
    return data || []
  },

  async getByLanguage(language: string, limit = 100, offset = 0): Promise<CodeVaultEntry[]> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('*')
      .eq('language', language)
      .order('name', { ascending: true })
      .range(offset, offset + limit - 1)
    if (error) return []
    return data || []
  },

  async search(query: string, limit = 50): Promise<CodeVaultEntry[]> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('*')
      .or(`name.ilike.%${query}%,description.ilike.%${query}%,tags.cs.{${query}}`)
      .order('name', { ascending: true })
      .limit(limit)
    if (error) { console.error('vault search error:', error); return [] }
    return data || []
  },

  async getBySlug(slug: string): Promise<CodeVaultEntry | null> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('*')
      .eq('slug', slug)
      .single()
    if (error) return null
    return data
  },

  async getById(id: string): Promise<CodeVaultEntry | null> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('*')
      .eq('id', id)
      .single()
    if (error) return null
    return data
  },

  async getCategories(): Promise<{ component_type: string; count: number }[]> {
    // Use RPC for grouped count if available, otherwise use distinct + count
    const { data, error } = await supabase
      .from('code_vault')
      .select('component_type')
      .not('component_type', 'is', null)
      .limit(5000) // Cap for performance
    if (error) return []

    // Group and count in JS (fast enough for ~5k)
    const counts: Record<string, number> = {}
    for (const item of data || []) {
      const type = item.component_type || 'misc'
      counts[type] = (counts[type] || 0) + 1
    }

    return Object.entries(counts)
      .map(([component_type, count]) => ({ component_type, count }))
      .sort((a, b) => b.count - a.count)
  },

  async getLanguages(): Promise<{ language: string; count: number }[]> {
    const { data, error } = await supabase
      .from('code_vault')
      .select('language')
      .not('language', 'is', null)
      .limit(5000)
    if (error) return []

    const counts: Record<string, number> = {}
    for (const item of data || []) {
      const lang = item.language || 'unknown'
      counts[lang] = (counts[lang] || 0) + 1
    }

    return Object.entries(counts)
      .map(([language, count]) => ({ language, count }))
      .sort((a, b) => b.count - a.count)
  },

  async getStats(): Promise<{ total: number; languages: number; types: number; totalLines: number }> {
    // Get total count efficiently
    const { count, error } = await supabase
      .from('code_vault')
      .select('*', { count: 'exact', head: true })

    if (error) return { total: 0, languages: 0, types: 0, totalLines: 0 }

    // Get all entries for accurate line count (paginated)
    let totalLines = 0
    const languages = new Set<string>()
    const types = new Set<string>()
    let offset = 0
    const batchSize = 1000

    while (offset < (count || 0)) {
      const { data } = await supabase
        .from('code_vault')
        .select('language, component_type, lines_of_code')
        .range(offset, offset + batchSize - 1)

      for (const item of data || []) {
        if (item.language) languages.add(item.language)
        if (item.component_type) types.add(item.component_type)
        totalLines += item.lines_of_code || 0
      }

      if (!data || data.length < batchSize) break
      offset += batchSize
    }

    return {
      total: count || 0,
      languages: languages.size,
      types: types.size,
      totalLines
    }
  }
}

// System Metrics
export const systemMetricsDb = {
  async getRecent(limit = 100): Promise<any[]> {
    const { data, error } = await supabase
      .from('system_metrics')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(limit)
    if (error) { console.error('systemMetrics getRecent error:', error); return [] }
    return data || []
  },

  async getRange(start: Date, end: Date): Promise<any[]> {
    const { data, error } = await supabase
      .from('system_metrics')
      .select('*')
      .gte('timestamp', start.toISOString())
      .lte('timestamp', end.toISOString())
      .order('timestamp', { ascending: true })
    if (error) { console.error('systemMetrics getRange error:', error); return [] }
    return data || []
  },

  async getLatest(): Promise<any | null> {
    const { data, error } = await supabase
      .from('system_metrics')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(1)
      .single()
    if (error) return null
    return data
  }
}

// LLM Calls
export const llmCallsDb = {
  async getRecent(limit = 100): Promise<any[]> {
    const { data, error } = await supabase
      .from('llm_calls')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('llmCalls getRecent error:', error); return [] }
    return data || []
  },

  async getByTask(taskId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('llm_calls')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true })
    if (error) { console.error('llmCalls getByTask error:', error); return [] }
    return data || []
  },

  async getByModel(model: string, limit = 100): Promise<any[]> {
    const { data, error } = await supabase
      .from('llm_calls')
      .select('*')
      .eq('model', model)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('llmCalls getByModel error:', error); return [] }
    return data || []
  },

  async getCostSummary(days = 30): Promise<{ model: string; total_cost: number; call_count: number }[]> {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data, error } = await supabase
      .from('llm_calls')
      .select('model, cost')
      .gte('created_at', startDate.toISOString())

    if (error) { console.error('llmCalls getCostSummary error:', error); return [] }

    // Group and sum in JS
    const summary: Record<string, { total_cost: number; call_count: number }> = {}
    for (const call of data || []) {
      if (!summary[call.model]) {
        summary[call.model] = { total_cost: 0, call_count: 0 }
      }
      summary[call.model].total_cost += call.cost || 0
      summary[call.model].call_count += 1
    }

    return Object.entries(summary).map(([model, { total_cost, call_count }]) => ({
      model,
      total_cost,
      call_count
    }))
  }
}

// Daily Spend
export const dailySpendDb = {
  async getRecent(days = 30): Promise<any[]> {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data, error } = await supabase
      .from('daily_spend')
      .select('*')
      .gte('date', startDate.toISOString().split('T')[0])
      .order('date', { ascending: true })
    if (error) { console.error('dailySpend getRecent error:', error); return [] }
    return data || []
  },

  async getRange(start: Date, end: Date): Promise<any[]> {
    const startStr = start.toISOString().split('T')[0]
    const endStr = end.toISOString().split('T')[0]

    const { data, error } = await supabase
      .from('daily_spend')
      .select('*')
      .gte('date', startStr)
      .lte('date', endStr)
      .order('date', { ascending: true })
    if (error) { console.error('dailySpend getRange error:', error); return [] }
    return data || []
  },

  async getTotal(): Promise<number> {
    const { data, error } = await supabase
      .from('daily_spend')
      .select('amount')
    if (error) { console.error('dailySpend getTotal error:', error); return 0 }

    return (data || []).reduce((sum, record) => sum + (record.amount || 0), 0)
  }
}

// Task History
export const taskHistoryDb = {
  async getRecent(limit = 50): Promise<any[]> {
    const { data, error } = await supabase
      .from('task_history')
      .select('*')
      .eq('status', 'completed')
      .order('completed_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('taskHistory getRecent error:', error); return [] }
    return data || []
  },

  async getByUser(userId: string, limit = 50): Promise<any[]> {
    const { data, error } = await supabase
      .from('task_history')
      .select('*')
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('taskHistory getByUser error:', error); return [] }
    return data || []
  },

  async getStats(): Promise<{ total_by_status: Record<string, number>; avg_duration_seconds: number; total_tokens: number }> {
    const { data, error } = await supabase
      .from('task_history')
      .select('status, duration_seconds, tokens_used')

    if (error) { console.error('taskHistory getStats error:', error); return { total_by_status: {}, avg_duration_seconds: 0, total_tokens: 0 } }

    const total_by_status: Record<string, number> = {}
    let totalDuration = 0
    let totalTokens = 0

    for (const task of data || []) {
      total_by_status[task.status] = (total_by_status[task.status] || 0) + 1
      totalDuration += task.duration_seconds || 0
      totalTokens += task.tokens_used || 0
    }

    const avg_duration_seconds = (data?.length || 0) > 0 ? totalDuration / (data?.length || 1) : 0

    return {
      total_by_status,
      avg_duration_seconds,
      total_tokens: totalTokens
    }
  },

  async getAggregates(): Promise<{ completed_tasks: number; total_tokens: number; total_cost_usd: number }> {
    const { data, error } = await supabase
      .from('task_history')
      .select('status, tokens_used, cost_usd')

    if (error) {
      console.error('taskHistory getAggregates error:', error)
      return { completed_tasks: 0, total_tokens: 0, total_cost_usd: 0 }
    }

    let completedTasks = 0
    let totalTokens = 0
    let totalCost = 0

    for (const task of data || []) {
      if (task.status?.toLowerCase() === 'completed') {
        completedTasks++
      }
      totalTokens += task.tokens_used || 0
      totalCost += task.cost_usd || 0
    }

    return {
      completed_tasks: completedTasks,
      total_tokens: totalTokens,
      total_cost_usd: totalCost
    }
  }
}

// Knowledge Base
export const knowledgeDb = {
  async getAll(limit = 500): Promise<any[]> {
    const { data, error } = await supabase
      .from('knowledge_base')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('knowledge getAll error:', error); return [] }
    return data || []
  },

  async getByType(sourceType: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('knowledge_base')
      .select('*')
      .eq('source_type', sourceType)
      .order('created_at', { ascending: false })
    if (error) { console.error('knowledge getByType error:', error); return [] }
    return data || []
  },

  async search(query: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('knowledge_base')
      .select('*')
      .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
      .order('created_at', { ascending: false })
    if (error) { console.error('knowledge search error:', error); return [] }
    return data || []
  },

  async add(title: string, content: string, sourceType: string, tags: string[] = []): Promise<any | null> {
    const { data, error } = await supabase
      .from('knowledge_base')
      .insert({
        title,
        content,
        source_type: sourceType,
        tags
      })
      .select()
      .single()
    if (error) { console.error('knowledge add error:', error); return null }
    return data
  }
}

// Custom Agents - Dedicated table for agent blueprints
export interface CustomAgent {
  id: string
  user_id: string
  name: string
  icon: string
  color: string
  network: string
  model: string
  system_prompt: string | null
  skills: string[]
  temperature: number
  max_tokens: number
  enabled: boolean
  created_at: string
  updated_at: string
}

export const customAgentsDb = {
  async getAll(): Promise<CustomAgent[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []

    const { data, error } = await supabase
      .from('custom_agents')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
    if (error) { console.error('customAgents getAll error:', error); return [] }
    return data || []
  },

  async getById(id: string): Promise<CustomAgent | null> {
    const { data, error } = await supabase
      .from('custom_agents')
      .select('*')
      .eq('id', id)
      .single()
    if (error) return null
    return data
  },

  async create(agent: Omit<CustomAgent, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<CustomAgent | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('custom_agents')
      .insert({ ...agent, user_id: user.id })
      .select()
      .single()
    if (error) { console.error('customAgents create error:', error); return null }
    return data
  },

  async update(id: string, updates: Partial<Omit<CustomAgent, 'id' | 'user_id' | 'created_at'>>): Promise<CustomAgent | null> {
    const { data, error } = await supabase
      .from('custom_agents')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (error) { console.error('customAgents update error:', error); return null }
    return data
  },

  async delete(id: string): Promise<boolean> {
    const { error } = await supabase.from('custom_agents').delete().eq('id', id)
    return !error
  },

  async getByNetwork(network: string): Promise<CustomAgent[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []

    const { data, error } = await supabase
      .from('custom_agents')
      .select('*')
      .eq('user_id', user.id)
      .eq('network', network)
      .order('name', { ascending: true })
    if (error) return []
    return data || []
  }
}

// Pipeline Configs - Dedicated table for visual pipeline configurations
export interface PipelineConfig {
  id: string
  user_id: string
  name: string
  description: string | null
  nodes: Record<string, unknown>[]
  connections: Record<string, unknown>[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export const pipelineConfigsDb = {
  async getAll(): Promise<PipelineConfig[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []

    const { data, error } = await supabase
      .from('pipeline_configs')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
    if (error) { console.error('pipelineConfigs getAll error:', error); return [] }
    return data || []
  },

  async getById(id: string): Promise<PipelineConfig | null> {
    const { data, error } = await supabase
      .from('pipeline_configs')
      .select('*')
      .eq('id', id)
      .single()
    if (error) return null
    return data
  },

  async create(config: Omit<PipelineConfig, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<PipelineConfig | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('pipeline_configs')
      .insert({ ...config, user_id: user.id })
      .select()
      .single()
    if (error) { console.error('pipelineConfigs create error:', error); return null }
    return data
  },

  async update(id: string, updates: Partial<Omit<PipelineConfig, 'id' | 'user_id' | 'created_at'>>): Promise<PipelineConfig | null> {
    const { data, error } = await supabase
      .from('pipeline_configs')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (error) { console.error('pipelineConfigs update error:', error); return null }
    return data
  },

  async delete(id: string): Promise<boolean> {
    const { error } = await supabase.from('pipeline_configs').delete().eq('id', id)
    return !error
  },

  async setActive(id: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false

    // Deactivate all first
    await supabase
      .from('pipeline_configs')
      .update({ is_active: false })
      .eq('user_id', user.id)

    // Activate the selected one
    const { error } = await supabase
      .from('pipeline_configs')
      .update({ is_active: true, updated_at: new Date().toISOString() })
      .eq('id', id)
    return !error
  },

  async getActive(): Promise<PipelineConfig | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data, error } = await supabase
      .from('pipeline_configs')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single()
    if (error) return null
    return data
  }
}

// Research Sessions
export const researchDb = {
  async getActive(): Promise<any[]> {
    const { data, error } = await supabase
      .from('research_sessions')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
    if (error) { console.error('research getActive error:', error); return [] }
    return data || []
  },

  async getAll(limit = 100): Promise<any[]> {
    const { data, error } = await supabase
      .from('research_sessions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('research getAll error:', error); return [] }
    return data || []
  },

  async create(topic: string, goal: string): Promise<any | null> {
    const { data, error } = await supabase
      .from('research_sessions')
      .insert({
        topic,
        goal,
        status: 'active'
      })
      .select()
      .single()
    if (error) { console.error('research create error:', error); return null }
    return data
  },

  async addThought(sessionId: string, content: string, type: string): Promise<any | null> {
    const { data, error } = await supabase
      .from('research_thoughts')
      .insert({
        session_id: sessionId,
        content,
        type
      })
      .select()
      .single()
    if (error) { console.error('research addThought error:', error); return null }
    return data
  }
}

// LLM Traces - Claude's tool calls and deviations
export interface LLMTrace {
  id: string
  session_id: string
  trace_type: 'tool_call' | 'pre_tool' | 'deviation' | 'correction' | 'plan'
  tool_name: string | null
  tool_input: Record<string, unknown> | null
  tool_output: string | null
  plan_text: string | null
  error_message: string | null
  duration_ms: number | null
  success: boolean
  deviation_type: string | null
  tags: string[] | null
  created_at: string
}

export const llmTracesDb = {
  async getRecent(limit = 100): Promise<LLMTrace[]> {
    const { data, error } = await supabaseService
      .from('llm_traces')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('llmTraces getRecent error:', error); return [] }
    return data || []
  },

  async getByType(traceType: string, limit = 100): Promise<LLMTrace[]> {
    const { data, error } = await supabaseService
      .from('llm_traces')
      .select('*')
      .eq('trace_type', traceType)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) return []
    return data || []
  },

  async getBySession(sessionId: string): Promise<LLMTrace[]> {
    const { data, error } = await supabaseService
      .from('llm_traces')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
    if (error) return []
    return data || []
  },

  async getStats(hours = 24): Promise<any> {
    const { data, error } = await supabaseService.rpc('get_trace_stats', { p_hours: hours })
    if (error) { console.error('getStats error:', error); return [] }
    return data || []
  },

  async getCount(): Promise<number> {
    const { count, error } = await supabaseService
      .from('llm_traces')
      .select('*', { count: 'exact', head: true })
    if (error) { console.error('llmTraces getCount error:', error); return 0 }
    return count || 0
  },

  async getFailurePatterns(hours = 168, minOccurrences = 3): Promise<any> {
    const { data, error } = await supabaseService.rpc('find_failure_patterns', {
      p_hours: hours,
      p_min_occurrences: minOccurrences
    })
    if (error) { console.error('getFailurePatterns error:', error); return [] }
    return data || []
  }
}

// Claude Memories - RAG memory system
export interface ClaudeMemory {
  id: string
  content: string
  category: string
  severity: string
  tags: string[] | null
  embedding?: number[] | null  // Optional - not always selected
  last_accessed: string | null
  access_count: number
  created_at: string
  updated_at?: string  // Optional - column may not exist
  // Similarity/search fields - only present from semantic search results
  similarity?: number  // 0.35-1.0 from v4 search
  composite_score?: number  // Weighted ranking score
  confidence?: number  // 0-1 confidence from local data
  metadata?: {
    helpful_count?: number
    unhelpful_count?: number
    last_helpful?: string
    last_unhelpful?: string
    important?: boolean
    marked_important_at?: string | null
    resolved?: boolean
    resolved_at?: string | null
    resolved_reason?: string | null
  } | null
}

export type MemorySortOption = 'recent' | 'accessed' | 'last_used' | 'severity' | 'resolved' | 'helpful' | 'unhelpful'

export const claudeMemoriesDb = {
  async getRecent(limit = 100): Promise<ClaudeMemory[]> {
    return this.getSorted('recent', limit)
  },

  async getSorted(sort: MemorySortOption = 'recent', limit = 100, offset = 0): Promise<ClaudeMemory[]> {
    let query = supabaseService
      .from('claude_memories')
      .select('id, content, category, severity, tags, last_accessed, access_count, created_at, metadata')

    switch (sort) {
      case 'accessed':
        query = query.order('access_count', { ascending: false })
        break
      case 'last_used':
        query = query.order('last_accessed', { ascending: false, nullsFirst: false })
        break
      case 'severity':
        // Custom order: critical > warning > info
        query = query.order('severity', { ascending: true }) // alphabetical puts critical first
        break
      case 'resolved':
        // Fetch all then sort client-side by resolved status
        query = query.order('created_at', { ascending: false })
        break
      case 'helpful':
      case 'unhelpful':
        // Fetch all then sort client-side by feedback counts
        query = query.order('created_at', { ascending: false })
        break
      case 'recent':
      default:
        query = query.order('created_at', { ascending: false })
    }

    const { data, error } = await query.range(offset, offset + limit - 1)
    if (error) { console.error('claudeMemories getSorted error:', error); return [] }

    // Client-side sort for resolved (resolved first, then by date)
    if (sort === 'resolved' && data) {
      return data.sort((a, b) => {
        const aResolved = a.metadata?.resolved ? 1 : 0
        const bResolved = b.metadata?.resolved ? 1 : 0
        if (aResolved !== bResolved) return bResolved - aResolved // resolved first
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    }

    // Client-side sort for helpful (most helpful first)
    if (sort === 'helpful' && data) {
      return data.sort((a, b) => {
        const aHelpful = a.metadata?.helpful_count || 0
        const bHelpful = b.metadata?.helpful_count || 0
        return bHelpful - aHelpful
      })
    }

    // Client-side sort for unhelpful (most unhelpful first)
    if (sort === 'unhelpful' && data) {
      return data.sort((a, b) => {
        const aUnhelpful = a.metadata?.unhelpful_count || 0
        const bUnhelpful = b.metadata?.unhelpful_count || 0
        return bUnhelpful - aUnhelpful
      })
    }

    return data || []
  },

  async search(query: string, limit = 20): Promise<ClaudeMemory[]> {
    // Use semantic search via log-server which calls claude_memory.py with v4
    // This gives us proper similarity scores via embeddings
    try {
      const res = await fetch(`/api/claude-logs/api/memories/search?q=${encodeURIComponent(query)}&limit=${limit}`)
      if (res.ok) {
        const data = await res.json()
        if (data.memories && data.memories.length > 0) {
          return data.memories
        }
      }
    } catch (e) {
      console.warn('Semantic search unavailable, falling back to text search')
    }

    // Fallback to Supabase text search (no similarity scores)
    const { data, error } = await supabaseService
      .from('claude_memories')
      .select('id, content, category, severity, tags, last_accessed, access_count, created_at, metadata')
      .ilike('content', `%${query}%`)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) { console.error('claudeMemories search error:', error); return [] }
    return data || []
  },

  async searchWithTags(query: string, tags: string[], limit = 20): Promise<ClaudeMemory[]> {
    const { data, error } = await supabaseService.rpc('search_memories_v2', {
      query_text: query,
      filter_tags: tags,
      match_count: limit
    })
    if (error) { console.error('claudeMemories searchWithTags error:', error); return [] }
    return data || []
  },

  async getStats(): Promise<any> {
    const { data, error } = await supabaseService.rpc('get_memory_stats')
    if (error) { console.error('claudeMemories getStats error:', error); return null }
    return data
  },

  async getCount(): Promise<number> {
    const { count, error } = await supabaseService
      .from('claude_memories')
      .select('*', { count: 'exact', head: true })
    if (error) { console.error('claudeMemories getCount error:', error); return 0 }
    return count || 0
  },

  async getStaleCount(daysOld = 30): Promise<number> {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - daysOld)
    const { count, error } = await supabaseService
      .from('claude_memories')
      .select('*', { count: 'exact', head: true })
      .lt('last_accessed', cutoff.toISOString())
    if (error) { console.error('claudeMemories getStaleCount error:', error); return 0 }
    return count || 0
  },

  async getByCategory(category: string, limit = 100): Promise<ClaudeMemory[]> {
    const { data, error } = await supabaseService
      .from('claude_memories')
      .select('id, content, category, severity, tags, last_accessed, access_count, created_at, metadata')
      .eq('category', category)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) return []
    return data || []
  },

  async getBySeverity(severity: string, limit = 100): Promise<ClaudeMemory[]> {
    const { data, error } = await supabaseService
      .from('claude_memories')
      .select('id, content, category, severity, tags, last_accessed, access_count, created_at, metadata')
      .eq('severity', severity)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) return []
    return data || []
  },

  async getStale(days = 30, limit = 100): Promise<ClaudeMemory[]> {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - days)

    const { data, error } = await supabaseService
      .from('claude_memories')
      .select('id, content, category, severity, tags, last_accessed, access_count, created_at, metadata')
      .or(`last_accessed.is.null,last_accessed.lt.${cutoff.toISOString()}`)
      .order('last_accessed', { ascending: true, nullsFirst: true })
      .limit(limit)
    if (error) return []
    return data || []
  },

  async markImportant(memoryId: string, important: boolean): Promise<boolean> {
    // Use RPC to merge metadata properly (preserves existing metadata fields)
    const { error } = await supabaseService.rpc('update_memory_importance', {
      memory_id: memoryId,
      is_important: important
    })

    if (error) {
      console.error('markImportant error:', error)
      return false
    }
    return true
  }
}

// Flooring Bot - Conversations, Clients, Messages
export const flooringBotDb = {
  // Expose supabase client for direct queries
  supabase,
  // Get all conversations with client info and latest message
  async getConversations(): Promise<any[]> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        client:clients(*),
        messages(content, created_at, sender_type)
      `)
      .eq('status', 'active')
      .order('updated_at', { ascending: false })

    if (error) {
      console.error('getConversations error:', error)
      return []
    }

    // Process to match UI format
    return (data || []).map((conv: any) => {
      const messages = conv.messages || []
      const lastMsg = messages[messages.length - 1]

      return {
        id: conv.id,
        title: conv.client?.name || 'Unknown Client',
        participants: [
          {
            id: 'client',
            name: conv.client?.name || 'Client',
            role: 'user',
            email: conv.client?.email || conv.client?.gmail || null,
            phone: conv.client?.phone || null
          },
          { id: 'bot-1', name: 'AI Assistant', role: 'ai' },
          { id: 'support', name: 'You', role: 'support' }
        ],
        messages: [], // Will be loaded separately when conversation selected
        lastActivity: new Date(conv.updated_at),
        lastMessage: lastMsg?.content || '',
        lastMessageAt: lastMsg ? new Date(lastMsg.created_at) : new Date(conv.created_at),
        unread: lastMsg?.sender_type === 'client', // Mark unread if last message from client
        isArchived: false,
        channel: conv.channel || 'sms',
        channelId: conv.channel_id,
        metadata: conv.metadata || {}
      }
    })
  },

  // Get messages for a specific conversation
  async getMessages(conversationId: string): Promise<any[]> {
    const { data, error} = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })

    if (error) {
      console.error('getMessages error:', error)
      return []
    }

    return (data || []).map((msg: any) => ({
      id: msg.id,
      conversationId: msg.conversation_id,
      participantId: msg.sender_type === 'client' ? 'client' : msg.sender_type === 'bot' ? 'bot-1' : 'support',
      content: msg.content,
      timestamp: new Date(msg.created_at),
      isRead: true,
      metadata: msg.metadata || {}
    }))
  },

  // Send a message (client response)
  async sendMessage(conversationId: string, content: string, senderType: 'client' | 'bot' = 'client'): Promise<any | null> {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_type: senderType,
        content
      })
      .select()
      .single()

    if (error) {
      console.error('sendMessage error:', error)
      return null
    }

    // Update conversation updated_at
    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId)

    return data
  },

  // Subscribe to new messages in a conversation
  subscribeToMessages(conversationId: string, callback: (message: any) => void) {
    return supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => callback(payload.new)
      )
      .subscribe()
  },

  // Subscribe to conversation updates
  subscribeToConversations(callback: (conversation: any) => void) {
    return supabase
      .channel('conversations:all')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => callback(payload.new)
      )
      .subscribe()
  },

  // Archive a conversation
  async archiveConversation(conversationId: string): Promise<boolean> {
    const { error } = await supabase
      .from('conversations')
      .update({ status: 'archived', updated_at: new Date().toISOString() })
      .eq('id', conversationId)
    return !error
  },

  // Delete a conversation (and its messages via cascade)
  async deleteConversation(conversationId: string): Promise<boolean> {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId)
    return !error
  }
}
